/**
 * User Growth API
 * Operational user-growth dashboard endpoints (admin only).
 */

import apiClient from './client';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';

export interface GrowthSummary {
  today_new_users: number;
  today_active_regular: number;
  today_active_total: number;
  new_users_7d: number;
  d1_retention_7d: number | null;
}

export interface DailyGrowthRow {
  date: string;
  new_users_ios: number;
  new_users_android: number;
  new_users_unknown: number;
  active_regular: number;
  active_team: number;
  cumulative_regular: number;
  cumulative_total: number;
}

export interface ActiveUserRow {
  date: string;
  user_id: string;
  display_name: string;
  classification: string;
  registered_at: string | null;
  is_new_user: boolean;
  platform: string | null;
  sessions: number;
  runs: number;
}

export interface GrowthFunnel {
  registered: number;
  created_self_chart: number;
  first_conversation: number;
  trial_started: number;
  paid: number;
  median_hours_to_chart: number | null;
}

export interface CohortRow {
  week_start: string;
  cohort_size: number;
  retention: number[];
}

export interface RetentionResponse {
  d7: number | null;
  d30: number | null;
  dau_mau: number | null;
  cohorts: CohortRow[];
}

export interface FunnelStage {
  stage: string;
  count: number;
}

export interface ShareLoopResponse {
  window_start: string;
  window_end: string;
  shares_created: number;
  distinct_sharers: number;
  secondary_shares: number;
  claimed_sessions: number;
  funnel: FunnelStage[];
  public_shares_created: number;
  public_share_views: number;
}

export interface PushDailyRow {
  date: string;
  sent: number;
  delivered: number;
  clicked: number;
}

export interface PushFunnelResponse {
  window_start: string;
  window_end: string;
  sent: number;
  delivered: number;
  clicked: number;
  failed: number;
  ctr: number | null;
  daily: PushDailyRow[];
  opened?: number | null;
  opened_users?: number | null;
  open_rate?: number | null;
  opened_source?: string;
}

export type AcquisitionChannel = 'referral' | 'affiliate' | 'promo' | 'organic';

export interface AcquisitionChannelRow {
  channel: AcquisitionChannel;
  new_users: number;
  converted_users: number;
  conversion_rate: number | null;
}

export interface AcquisitionChannelsResponse {
  window_start: string;
  window_end: string;
  channels: AcquisitionChannelRow[];
  affiliate_commission_pending_cents: number;
  affiliate_commission_cleared_cents: number;
}

/**
 * Shared window params: either preset `days`, or a custom closed range
 * `start_date`/`end_date` (YYYY-MM-DD, server timezone). When both are sent,
 * the backend gives precedence to start/end.
 */
export interface RangeParams {
  days?: number;
  start_date?: string;
  end_date?: string;
  exclude_team?: boolean;
}

const BASE = '/user-growth';

function withTeamExclusionDefault<T extends RangeParams>(params: T): T & { exclude_team: boolean } {
  return {
    ...params,
    exclude_team: params.exclude_team ?? DEFAULT_EXCLUDE_TEAM,
  };
}

export const userGrowthApi = {
  getSummary: async (): Promise<GrowthSummary> => {
    const response = await apiClient.get(`${BASE}/summary`);
    return response.data;
  },

  getDaily: async (range: RangeParams): Promise<DailyGrowthRow[]> => {
    const response = await apiClient.get(`${BASE}/daily`, { params: withTeamExclusionDefault(range) });
    return response.data;
  },

  getActiveUsers: async (
    params: RangeParams & {
      classification?: string;
      exclude_team?: boolean;
    },
  ): Promise<ActiveUserRow[]> => {
    const response = await apiClient.get(`${BASE}/active-users`, { params: withTeamExclusionDefault(params) });
    return response.data;
  },

  getFunnel: async (range: RangeParams): Promise<GrowthFunnel> => {
    const response = await apiClient.get(`${BASE}/funnel`, { params: withTeamExclusionDefault(range) });
    return response.data;
  },

  getRetention: async (): Promise<RetentionResponse> => {
    const response = await apiClient.get(`${BASE}/retention`);
    return response.data;
  },

  getShareLoop: async (range: RangeParams): Promise<ShareLoopResponse> => {
    const response = await apiClient.get(`${BASE}/share-loop`, { params: withTeamExclusionDefault(range) });
    return response.data;
  },

  getPushFunnel: async (range: RangeParams): Promise<PushFunnelResponse> => {
    const response = await apiClient.get(`${BASE}/push-funnel`, { params: withTeamExclusionDefault(range) });
    return response.data;
  },

  getAcquisitionChannels: async (
    range: RangeParams,
  ): Promise<AcquisitionChannelsResponse> => {
    const response = await apiClient.get(`${BASE}/acquisition-channels`, { params: withTeamExclusionDefault(range) });
    return response.data;
  },
};
